import React from 'react';

import * as Mustache from 'mustache';
import * as Sentry from '@sentry/browser';

import { useRuntimeContext } from '../context/runtime-context';
import { neo_eval } from "../utils/eval";

const FORBIDDEN_TAGS = [
    'SCRIPT',
    'EMBED',
    'BUTTON',
    'INPUT'
];

export const sanitize = (element) => {
    // If element is text, just return it
    if (element.nodeType && element.nodeType === window.Node.TEXT_NODE) {
        return element;
    }

    let attributes = element.getAttributeNames();

    for (let name of attributes) { // eslint-disable-line no-unused-vars
        if ((/^on/i).test(name)) {
            element.removeAttribute(name);
        }
    }

    return element;
};

export const contextify = (content, context={}) => {
    Mustache.tags = ['%', '%'];

    try {
        content = Mustache.render(content, context);
        return neo_eval(content.trim());
    } catch(e) {
        Sentry.configureScope(scope => {
            scope.setExtra('content', content);
            scope.setExtra('context', context);
        });
        Sentry.captureException(e);

        return content;
    }
};

export default function HTMLContent({ content, className, asContainer = "div" }) {
    const { context } = useRuntimeContext();
    const rootElementRef = React.useRef(null);
    const contentElement = React.useRef(null);

    React.useEffect(() => {
        let parser = new window.DOMParser();
        let contextified_content = contextify(content, context);
        let doc = parser.parseFromString(contextified_content, 'text/html');
        if(!doc) {
            // How do we warn the organization that there is a problem?
            Sentry.configureScope(scope => {
                scope.setExtra('html-content', content);
                scope.setExtra('contextified-content', contextified_content);
            });
            Sentry.captureException(new Error(`[HTMLContent] Could not parse HTML content`));
            return;
        }

        let containerDiv = document.createElement(asContainer);

        // Child nodes includes text nodes
        let children = Array.from(doc.body.childNodes);

        for (let child of children) { // eslint-disable-line no-unused-vars
            if (FORBIDDEN_TAGS.includes(child.tagName)) {
                continue;
            }

            let sanitizedChild = sanitize(child);
            containerDiv.appendChild(sanitizedChild);
        }
        contentElement.current = containerDiv;

        let content_reference = contentElement.current;
        let root_reference = rootElementRef.current;

        if (root_reference) {
            root_reference.appendChild(content_reference);
        }

        return () => root_reference.removeChild(content_reference);
    }, [content, context]);

    return (
        <div ref={rootElementRef} className={className}></div>
    );
}
